<template>
    <div class="uti-account-details">
        <div class="uti-account-details__info">
            To get started with lifetime access to Pocket
            Prep's ASE exam prep, please complete your account and purchase info.
        </div>
        <PocketInput
            v-model="firstName"
            class="uti-account-details__input"
            :class="{
                'uti-account-details__input--password': showPasswordSignIn,
            }"
            field-type=""
            label="First Name"
            aria-required="true"
            :error="!!errorFields.length"
            :disabled="!!currentUser && !!currentUser.firstName"
            @keydown.enter="submitAccountDetails"
        />
        <PocketInput
            v-model="lastName"
            class="uti-account-details__input"
            field-type=""
            label="Last Name"
            aria-required="true"
            :error="!!errorFields.length"
            :disabled="!!currentUser && !!currentUser.lastName"
            @keydown.enter="submitAccountDetails"
        />
        <PocketSelect
            v-if="campusOptions.length"
            v-model="campus"
            class="uti-account-details__input"
            :class="{
                'uti-account-details__input--password': showPasswordSignIn,
            }"
            label="Campus"
            placeholder="Select Campus"
            :data="campusOptions"
        />
        <PocketInput
            v-model="email"
            class="uti-account-details__input"
            :class="{
                'uti-account-details__input--password': showPasswordSignIn,
            }"
            field-type="email"
            label="Email"
            :error="emailError"
            :disabled="!!currentUser && !!currentUser.username"
            @keydown.enter="submitAccountDetails"
        />
        <PocketInput
            v-if="createPassword"
            ref="passwordInput"
            v-model="password"
            :error="errorFields.length > 0"
            class="uti-account-details__input"
            aria-required="true"
            label="Create Password"
            labelSubtext="10+ characters, including uppercase and a number"
            field-type="password"
            :disabled="!!currentUser"
            @keydown.enter="submitAccountDetails"
        />
        <Errors
            v-if="errors.length"
            class="uti-account-details__errors"
            :class="{ 'uti-account-details__errors--password-errors': showPasswordErrorHeader }"
            :errors="errors"
            :error-header="errorsHeader"
        />
        <div v-if="!currentUser" class="uti-account-details__create-password">
            <template v-if="!createPassword">
                <PocketButton
                    class="uti-account-details__create-password-link"
                    type="tertiary-small"
                    @click="toggleCreatePassword"
                    @keydown.enter="toggleCreatePassword"
                    @mousedown.prevent
                >
                    Create a password
                </PocketButton>
                instead of using a link to sign in
            </template>
            <template v-else>
                <PocketButton
                    class="uti-account-details__create-password-link"
                    type="tertiary-small"
                    @click="toggleCreatePassword"
                    @keydown.enter="toggleCreatePassword"
                    @mousedown.prevent
                >
                    Sign in with a link
                </PocketButton>
                instead of creating a password
            </template>
        </div>
        <div class="uti-account-details__legal">
            <Checkbox
                v-model="agreed"
                class="uti-account-details__legal-checkbox"
                :disabled="agreeDisabled"
                aria-label="I agree to the privacy policy and terms of service, required"
            />
            <div class="uti-account-details__legal-text">
                I agree to the
                <a
                    target="_blank"
                    :href="`https://www.pocketprep.com/privacy-policy?${buildUTMParamString({
                        utm_campaign: 'app_settings',
                        utm_content: 'account_details_privacy_policy'
                    })}`"
                    class="uti-account-details__legal-text-link"
                >privacy policy</a>
                and
                <a
                    target="_blank"
                    :href="`https://www.pocketprep.com/terms-of-service?${buildUTMParamString({
                        utm_campaign: 'app_settings',
                        utm_content: 'account_details_terms'
                    })}`"
                    class="uti-account-details__legal-text-link"
                >terms of service</a>
            </div>
        </div>
        <PocketButton
            class="uti-account-details__submit-account-details"
            :disabled="!firstName.trim()
                || !lastName.trim() || !email || !agreed || !campus || (createPassword && !password)"
            :is-loading="isLoading"
            @click="submitAccountDetails"
            @keydown.enter="submitAccountDetails"
        >
            Continue to Payment <Icon type="arrow" />
        </PocketButton>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'
import { isValidEmail, isValidPassword, passwordPolicyErrors } from '@/utils'
import { examMetadataModule } from '@/store/examMetadata/module'
import { buildUTMParamString } from '@/store/utils'

const {
    Input: PocketInput,
    Button: PocketButton,
    Icon,
    Select: PocketSelect,
    Checkbox,
    Errors,
} = UIKit

const props = defineProps<{
    examGuids: string[]
    orgIdNameLib: { [orgId: string]: string } | null
}>()

const emit = defineEmits<{
    submit: [submitInfo: { password: string }]
}>()

const route = useRoute()
const router = useRouter()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const campus = ref<{ label: string; value: string } | null>(null)
const password = ref('')
const createPassword = ref(false)
const showPasswordSignIn = ref(false)
const isLoading = ref(false)
const emailError = ref(false)
const agreed = ref(false)
const agreeDisabled = ref(false)
const errors = ref<string[]>([])
const errorFields = ref<string[]>([])
const errorsHeader = ref('')
const showPasswordErrorHeader = ref(false)

const passwordInput = ref<InstanceType<typeof UIKit.Input> | null>(null)

const currentUser = computed(() => {
    return userModule.state.user
})

const examMetadata = computed(() => {
    return examMetadataModule.getters.getExamMetadata()
})

const exam = computed(() => {
    return examMetadata.value.find(examMetadataObj => examMetadataObj.examGuid === props.examGuids[0])
})

const campusOptions = computed(() => {
    if (!props.orgIdNameLib) {
        return []
    }
    return Object.entries(props.orgIdNameLib).map(orgIdName => ({
        label: orgIdName[1],
        value: orgIdName[0],
    }))
})

onMounted(() => {
    const existingUser = userModule.state.user
    firstName.value = existingUser?.firstName || ''
    lastName.value = existingUser?.lastName || ''
    email.value = existingUser?.email || ''
    createPassword.value = !!password.value
})

const toggleCreatePassword = () => {
    createPassword.value = !createPassword.value

    if (!createPassword.value) {
        password.value = ''
        errorFields.value = []
        errors.value = []
        errorsHeader.value = ''
        showPasswordErrorHeader.value = false
    } else {
        setTimeout(() => {
            const passwordInputRef = passwordInput.value
            const passwordInputEl = (passwordInputRef
                && '$el' in passwordInputRef
                && passwordInputRef.$el) as HTMLElement
            const inputEl = passwordInputEl?.querySelector('input')
            inputEl?.focus()
        }, 1)
    }
}

const submitAccountDetails = async () => {
    errors.value = []
    errorFields.value = []
    errorsHeader.value = ''
    showPasswordErrorHeader.value = false
    isLoading.value = true

    try {
        const currentUserState = userModule.state.user
        if (!firstName.value || !lastName.value) {
            if (!firstName.value) {
                errorFields.value.push('firstName')
            }
            if (!lastName.value) {
                errorFields.value.push('lastName')
            }
            errors.value.push('Please enter a first and last name.')
        }

        if (!campus.value) {
            errors.value.push('Must select a campus.')
        }

        if (!agreed.value) {
            errors.value.push('You must agree to the terms of service and privacy policy to continue.')
        }

        if (!isValidEmail(email.value)) {
            errorFields.value.push('email')
            errors.value.push('Invalid email address.')
        }

        if (createPassword.value && !isValidPassword(password.value)) {
            errorFields.value.push('password')
            showPasswordErrorHeader.value = errorFields.value.length === 1 && errorFields.value.includes('password')
            if (showPasswordErrorHeader.value) {
                errorsHeader.value = 'Password doesn`t meet the following requirements:'
            }

            const passwordErrors = passwordPolicyErrors(showPasswordErrorHeader.value, password.value)

            errors.value = [ ...errors.value, ...passwordErrors ]
        }

        if (!errors.value.length && exam.value) {
            if (currentUserState) {
                await userModule.actions.updateUser({
                    email: email.value,
                    firstName: firstName.value,
                    lastName: lastName.value,
                    password: password.value,
                    exam: exam.value,
                })
            } else {
                await userModule.actions.createUser({
                    email: email.value,
                    firstName: firstName.value,
                    lastName: lastName.value,
                    password: password.value,
                    exam: exam.value,
                })
            }

            emit('submit', {
                password: password.value,
            })
        }
    } catch (err) {
        const error = (err as { error?: string }).error || (err as { message?: string }).message
        if (!err) {
            errors.value.push('Unable to create or update account.')
        } else if (error) {
            errors.value.push(error)
        } else if (err instanceof Error || typeof err === 'string') {
            errors.value.push(err.toString())
        }

    } finally {
        isLoading.value = false
    }
}

watch(campus, () => {
    if (campus.value?.value && route.query.orgId !== campus.value.value) {
        router.replace({
            name: 'uti-discount',
            query: {
                ...route.query,
                orgId: campus.value.value,
            },
        })
    }
})
</script>

<style lang="scss" scoped>
.uti-account-details {
    width: 320px;
    margin: 9px auto 0;

    &__info {
        width: 320px;
        color: $slate-03;
        font-size: 14px;
        line-height: 19px;
        text-align: center;
        margin-bottom: 36px;
    }

    &__input {
        margin-bottom: 17px;

        &--password {
            margin-bottom: 21px;
        }
    }

    &__errors {
        margin-bottom: 17px;

        &--password-errors {
            :deep(.uikit-errors__error) {
                &::before {
                    content: '•';
                    display: inline-block;
                    margin-right: 5px;
                }
            }
        }
    }

    &__create-password {
        font-size: 13px;
        line-height: 18px;
        margin-bottom: 24px;
        margin-left: 11px;

        &--error {
            margin-bottom: 20px;
        }
    }

    &__create-password-link {
        font-weight: 500;
        font-size: 13px;
    }

    &__legal {
        font-size: 13px;
        line-height: 18px;
        margin-left: 4px;
        display: flex;
        align-items: center;
    }

    &__legal-checkbox {
        margin-right: 9px;
        vertical-align: middle;
        margin-bottom: 2px;
    }

    &__legal-text-link {
        color: $brand-blue;
        text-decoration: none;
    }

    &__submit-account-details {
        margin: 42px auto 30px;
        display: block;

        svg {
            margin-left: 10px;
        }
    }
}
</style>
